import React, { useState } from "react";
import { Card, Form, Badge, Button, Stack } from "react-bootstrap";
import "./ToDoList.css";

const ToDoList = ({
  tasks,
  tags,
  selectTask,
  toggleTaskCompletion,
  selectedTask,
}) => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("priority");

  const handleSearch = (e) => {
    setSearchKeyword(e.target.value);
  };

  const handleStatusFilter = (filter) => {
    setStatusFilter(filter);
  };

  const handleTagFilter = (e) => {
    setTagFilter(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  const filteredTasks = tasks.filter((task) => {
    const matchesKeyword = task.title
      .toLowerCase()
      .includes(searchKeyword.toLowerCase());
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "completed" && task.completed) ||
      (statusFilter === "incomplete" && !task.completed);
    const matchesTag = tagFilter === "all" || task.tags.includes(tagFilter);
    return matchesKeyword && matchesStatus && matchesTag;
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    switch (sortOrder) {
      case "deadline": {
        const timeA = a.deadline ? new Date(a.deadline).getTime() : Infinity;
        const timeB = b.deadline ? new Date(b.deadline).getTime() : Infinity;
        return timeA - timeB;
      }
      case "title":
        return a.title.localeCompare(b.title, "ja");
      case "priority":
      default: {
        const priorityOrder = { high: 1, medium: 2, low: 3 };
        return (
          (priorityOrder[a.priority] || 4) - (priorityOrder[b.priority] || 4)
        );
      }
    }
  });

  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    try {
      const date = new Date(deadline);
      if (Number.isNaN(date.getTime())) return null;
      return date.toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return null;
    }
  };

  return (
    <div className="todo-list-section">
      <Card className="filter-card shadow-sm">
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label className="filter-label">キーワード検索</Form.Label>
            <Form.Control
              type="text"
              placeholder="タイトル・メモから検索"
              value={searchKeyword}
              onChange={handleSearch}
            />
          </Form.Group>
          <Form.Label className="filter-label">ステータス</Form.Label>
          <div className="filter-segment mb-3">
            <Button
              variant={statusFilter === "all" ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => handleStatusFilter("all")}
            >
              すべて
            </Button>
            <Button
              variant={
                statusFilter === "incomplete" ? "success" : "outline-success"
              }
              size="sm"
              onClick={() => handleStatusFilter("incomplete")}
            >
              未完了
            </Button>
            <Button
              variant={
                statusFilter === "completed" ? "secondary" : "outline-secondary"
              }
              size="sm"
              onClick={() => handleStatusFilter("completed")}
            >
              完了済み
            </Button>
          </div>
          <Stack
            direction="vertical"
            gap={3}
            className="filter-stack flex-md-row flex-wrap"
          >
            <Form.Group className="flex-grow-1">
              <Form.Label className="filter-label">タグ</Form.Label>
              <Form.Select value={tagFilter} onChange={handleTagFilter} size="sm">
                <option value="all">すべてのタグ</option>
                {tags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="flex-grow-1">
              <Form.Label className="filter-label">並び替え</Form.Label>
              <Form.Select value={sortOrder} onChange={handleSortChange} size="sm">
                <option value="priority">優先度でソート</option>
                <option value="deadline">締め切りでソート</option>
                <option value="title">タイトルでソート</option>
              </Form.Select>
            </Form.Group>
          </Stack>
        </Card.Body>
      </Card>

      <div className="task-list">
        {sortedTasks.length === 0 ? (
          <div className="empty-state text-center">
            <i className="bi bi-inbox text-primary fs-1 mb-2"></i>
            <p className="fw-semibold mb-1">表示できるタスクがありません</p>
            <p className="text-muted mb-0">
              新規作成ボタンまたはフィルター条件を調整してください。
            </p>
          </div>
        ) : (
          sortedTasks.map((task) => {
            const deadlineLabel = formatDeadline(task.deadline);
            const priorityLabel = getPriorityLabel(task.priority);
            return (
              <Card
                key={task.id}
                onClick={() => selectTask(task)}
                className={`task-card ${getPriorityClass(task.priority)} ${
                  selectedTask && selectedTask.id === task.id ? "active" : ""
                } ${task.completed ? "completed" : ""}`}
              >
                <Card.Body className="d-flex flex-column flex-md-row align-items-start justify-content-between gap-3">
                  <div className="d-flex align-items-start gap-3">
                    <Form.Check
                      type="checkbox"
                      checked={task.completed}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleTaskCompletion(task.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="task-text flex-grow-1">
                      <div className="task-title fw-semibold mb-1">
                        {task.title}
                      </div>
                      {deadlineLabel && (
                        <div className="task-deadline text-muted small">
                          <i className="bi bi-calendar-event me-1"></i>
                          {deadlineLabel}
                        </div>
                      )}
                      {task.details && (
                        <div className="task-detail text-muted small">
                          <i className="bi bi-journal-text me-1"></i>
                          メモあり
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="task-meta text-md-end w-100">
                    <div className="mb-2 d-flex flex-wrap gap-1 justify-content-md-end">
                      {task.tags.length > 0 ? (
                        task.tags.map((tag) => (
                          <Badge key={tag} pill bg="info" className="tag-badge">
                            {tag}
                          </Badge>
                        ))
                      ) : (
                        <Badge bg="light" text="dark" pill className="tag-badge">
                          タグなし
                        </Badge>
                      )}
                    </div>
                    <Badge
                      bg={getPriorityBadgeClass(task.priority)}
                      className="priority-pill"
                    >
                      {priorityLabel || "未設定"}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

// 優先度ラベルの取得
const getPriorityLabel = (priority) => {
  switch (priority) {
    case "high":
      return "高";
    case "medium":
      return "中";
    case "low":
      return "低";
    default:
      return "";
  }
};

// 優先度バッジのクラス取得
const getPriorityBadgeClass = (priority) => {
  switch (priority) {
    case "high":
      return "danger";
    case "medium":
      return "warning";
    case "low":
      return "success";
    default:
      return "secondary";
  }
};

// 優先度に応じたクラス取得
const getPriorityClass = (priority) => {
  switch (priority) {
    case "high":
      return "priority-high";
    case "medium":
      return "priority-medium";
    case "low":
      return "priority-low";
    default:
      return "";
  }
};

export default ToDoList;
